import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Phone, Menu } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetClose,
  SheetTitle,
} from "@/components/ui/sheet";
import { company, navigation } from "@/data/content";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="navbar"
      className={`fixed inset-x-0 top-0 z-40 transition-colors duration-300 ${
        scrolled
          ? "bg-background/95 backdrop-blur-sm border-b border-border shadow-[0_1px_0_hsl(var(--border))]"
          : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 lg:px-10 transition-[height] duration-300"
        style={{ height: scrolled ? "64px" : "78px" }}
      >
        <Link to="/" className="flex flex-col leading-none" data-testid="nav-logo">
          <span className="text-lg font-bold tracking-[0.18em] text-foreground">
            {company.name}
          </span>
          <span className="mt-1 text-[11px] tracking-wide text-muted-foreground">
            {company.tagline}
          </span>
        </Link>

        <nav className="hidden items-center gap-8 lg:flex" data-testid="nav-links">
          {navigation.map((item) => (
            <Link
              key={item.label}
              to={item.href}
              className="text-sm font-medium text-foreground/80 transition-colors duration-200 hover:text-accent"
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="hidden items-center gap-5 lg:flex">
          <a
            href={company.phoneHref}
            data-testid="nav-phone"
            className="flex items-center gap-2 text-sm font-semibold text-foreground transition-colors duration-200 hover:text-accent"
          >
            <Phone className="h-4 w-4" />
            {company.phone}
          </a>
          <Button asChild data-testid="nav-cta" className="bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-5">
            <Link to="/#kontakt">Nezávazná nabídka</Link>
          </Button>
        </div>

        {/* Mobile menu */}
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <button
              data-testid="mobile-menu-trigger"
              className="flex h-11 w-11 items-center justify-center rounded-md text-foreground lg:hidden"
              aria-label="Otevřít menu"
            >
              <Menu className="h-6 w-6" />
            </button>
          </SheetTrigger>
          <SheetContent side="right" className="w-full max-w-full bg-card p-0 sm:max-w-md border-l border-border" data-testid="mobile-menu">
            <SheetTitle className="sr-only">Navigace</SheetTitle>
            <div className="flex h-full flex-col justify-between overflow-y-auto p-8">
              <div>
                <span className="text-base font-bold tracking-[0.18em]">{company.name}</span>
                <nav className="mt-10 flex flex-col gap-1">
                  {navigation.map((item) => (
                    <SheetClose asChild key={item.label}>
                      <Link
                        to={item.href}
                        data-testid={`mobile-nav-${item.label}`}
                        className="border-b border-border py-4 text-2xl font-medium text-foreground transition-colors hover:text-accent"
                      >
                        {item.label}
                      </Link>
                    </SheetClose>
                  ))}
                </nav>
                <div className="mt-8 flex flex-col gap-3">
                  <a href={company.phoneHref} className="flex items-center gap-2 text-base font-semibold">
                    <Phone className="h-4 w-4 text-accent" /> {company.phone}
                  </a>
                  <SheetClose asChild>
                    <Button asChild className="h-12 bg-primary text-primary-foreground hover:bg-primary/90">
                      <Link to="/#kontakt">Nezávazná nabídka</Link>
                    </Button>
                  </SheetClose>
                </div>
              </div>
              <figure className="mt-10">
                <img
                  src="./images/fasada-zluty-dum.jpg"
                  alt="Zateplená fasáda rodinného domu"
                  className="h-40 w-full rounded-md object-cover"
                  loading="lazy"
                />
                <figcaption className="mt-2 text-xs text-muted-foreground">
                  Zateplení rodinného domu · dokončená realizace
                </figcaption>
              </figure>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
};
